import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * Button — BRANDBOOK §6.1 (Sprint 28c hed_v3).
 *
 * Variants:
 *   primary    → lime bg + ink text (sayfa başına max 1, BRANDBOOK §2.5).
 *   secondary  → ink bg + krem text (ikincil primary).
 *   ghost      → transparent + ink text + g200 border (filtre, "İptal").
 *   destructive→ danger bg + paper text (reddet, sil).
 *   success    → success bg + paper text (sınırlı kullanım).
 *   outline    → ink border + ink text (legacy uyumu, ghost'a yakın).
 *   dark       → ink bg + krem text (secondary alias, legacy).
 *
 * Hover'da -1px translate (lift). Disabled'da lift kapanır.
 * BRANDBOOK §6.1 + §10.2 — focus-visible ink outline.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-custel-lg font-medium tracking-tight transition-all duration-custel-fast border border-transparent focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-ink disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 whitespace-nowrap hover:-translate-y-px active:translate-y-0',
  {
    variants: {
      variant: {
        primary: 'bg-custel-lime text-custel-ink hover:bg-custel-accent-hover',
        secondary: 'bg-custel-ink text-custel-krem hover:bg-custel-g700',
        ghost: 'bg-transparent text-custel-ink border-custel-border hover:border-custel-ink hover:bg-custel-g100',
        destructive: 'bg-custel-danger text-white hover:opacity-90',
        success: 'bg-custel-success text-white hover:opacity-90',
        // Legacy aliases (Phase J sonu temizlenebilir)
        outline: 'bg-transparent text-custel-ink border-custel-border hover:border-custel-ink hover:bg-custel-g100',
        dark: 'bg-custel-ink text-custel-krem hover:bg-custel-g700',
      },
      size: {
        sm: 'h-8 px-3 text-[13px]',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-5 text-[15px]',
        icon: 'h-9 w-9 p-0',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, fullWidth, leadingIcon, trailingIcon, loading, disabled, children, ...rest }, ref) => (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      disabled={disabled || loading}
      className={cn(buttonVariants({ variant, size, fullWidth }), className)}
      {...rest}
    >
      {loading ? (
        // BRANDBOOK §7.4 — lime üstünde spinner ink (currentColor zaten ink döner).
        <Loader2 size={size === 'sm' ? 12 : size === 'lg' ? 18 : 14} className="animate-spin" aria-hidden="true" />
      ) : (
        leadingIcon
      )}
      {children}
      {trailingIcon && !loading && trailingIcon}
    </button>
  ),
);
Button.displayName = 'Button';

export { buttonVariants };
